import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './context/AuthContext'

// Pages
import Login from './pages/Login'
import AdminDashboard from './pages/AdminDashboard'
import ClientDashboard from './pages/ClientDashboard'
import PrivacyPolicy from './pages/PrivacyPolicy'
import TermsAndConditions from './pages/TermsAndConditions'

// Components
import ProtectedRoute from './components/common/ProtectedRoute'
import SitePreview from './components/client/SitePreview'
import LoadingSpinner from './components/common/LoadingSpinner'
import { ThemeProvider } from './components/ui/theme-provider'
import { Toaster } from './components/ui/toaster'
import { Button } from './components/ui/button'
import { BrandingProvider } from './context/BrandingContext'

function App() {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <ThemeProvider defaultTheme="system" storageKey="webnest-ui-theme">
        <BrandingProvider>
          <div className="min-h-[100dvh] flex items-center justify-center bg-surface-50 dark:bg-dark-300">
            <LoadingSpinner size="lg" />
          </div>
        </BrandingProvider>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider defaultTheme="system" storageKey="webnest-ui-theme">
      <BrandingProvider>
      <div className="min-h-[100dvh] bg-surface-50 dark:bg-dark-300">
        <Routes>
          {/* Public routes */}
          <Route 
            path="/login" 
            element={
              user ? (
                <Navigate to={user.role === 'admin' ? '/admin' : '/client'} replace />
              ) : (
                <Login />
              )
            } 
          />

          {/* Admin routes */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          {/* Client site preview — full-page, no sidebar */}
          <Route
            path="/client/preview/:siteId"
            element={
              <ProtectedRoute requiredRole="client">
                <SitePreview />
              </ProtectedRoute>
            }
          />

          {/* Client routes */}
          <Route
            path="/client/*"
            element={
              <ProtectedRoute requiredRole="client">
                <ClientDashboard />
              </ProtectedRoute>
            }
          />

          {/* Legal pages — public, no auth required */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />

          {/* Default redirect */}
          <Route
            path="/"
            element={
              <Navigate 
                to={
                  user 
                    ? user.role === 'admin' 
                      ? '/admin' 
                      : '/client'
                    : '/login'
                } 
                replace 
              />
            }
          />

          {/* 404 fallback */}
          <Route
            path="*"
            element={
              <div className="min-h-[100dvh] flex items-center justify-center bg-surface-50 dark:bg-dark-300">
                <div className="text-center space-y-6">
                  <div className="space-y-2">
                    <h1 className="text-6xl font-bold text-neutral-300 dark:text-neutral-600">404</h1>
                    <p className="text-xl text-neutral-600 dark:text-neutral-400">Page not found</p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-500">
                      The page you're looking for doesn't exist.
                    </p>
                  </div>
                  <Button
                    onClick={() => window.history.back()}
                    variant="outline"
                  >
                    Go Back
                  </Button>
                </div>
              </div>
            }
          />
        </Routes>
        <Toaster />
      </div>
      </BrandingProvider>
    </ThemeProvider>
  )
}

export default App